import React, { useState } from 'react';
import {
  FaBookOpen,
  FaImage,
  FaCode,
  FaPalette,
  FaRocket,
  FaLightbulb,
  FaChevronDown,
  FaChevronUp,
  FaExternalLinkAlt,
  FaCloud,
  FaFolder,
  FaCubes,
  FaTags,
  FaCoins,
  FaCheckCircle,
  FaStar
} from 'react-icons/fa';
import './CreatorGuide.css';

const GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: FaRocket,
    content: `
      <h4>Welcome to NFT Creation on KleverChain!</h4>
      <p>Creating an NFT collection on KleverChain is straightforward. Here's what you'll need:</p>
      <ul>
        <li><strong>Collection Name & Ticker:</strong> A unique name and short symbol (3-10 characters)</li>
        <li><strong>Images:</strong> Your artwork files (PNG, JPG, GIF, or MP4)</li>
        <li><strong>Metadata:</strong> JSON files describing each NFT's traits and properties</li>
        <li><strong>KLV:</strong> Approximately 20,000 KLV to create a collection</li>
      </ul>
      <p>The process involves three main steps:</p>
      <ol>
        <li>Upload your images and metadata to IPFS</li>
        <li>Create your collection on KleverChain</li>
        <li>Configure an ITO (Initial Token Offering) to sell your NFTs</li>
      </ol>
    `
  },
  {
    id: 'ipfs-upload',
    title: 'Uploading to IPFS',
    icon: FaCloud,
    content: `
      <h4>What is IPFS?</h4>
      <p>IPFS (InterPlanetary File System) is a decentralized storage network. Unlike regular servers,
      files on IPFS are permanent and can't be modified or deleted, making it perfect for NFTs.</p>

      <h4>Recommended Services</h4>
      <div class="service-cards">
        <div class="service-card">
          <strong>Pinata</strong>
          <p>Free tier: 500MB storage, easy to use</p>
          <a href="https://www.pinata.cloud" target="_blank" rel="noopener noreferrer">pinata.cloud <span class="external-icon">↗</span></a>
        </div>
        <div class="service-card">
          <strong>NFT.Storage</strong>
          <p>Free & unlimited for NFT data</p>
          <a href="https://nft.storage" target="_blank" rel="noopener noreferrer">nft.storage <span class="external-icon">↗</span></a>
        </div>
        <div class="service-card">
          <strong>Web3.Storage</strong>
          <p>Free tier: 5GB, developer-friendly</p>
          <a href="https://web3.storage" target="_blank" rel="noopener noreferrer">web3.storage <span class="external-icon">↗</span></a>
        </div>
      </div>

      <h4>How to Upload to Pinata</h4>
      <ol>
        <li>Create a free account at <a href="https://www.pinata.cloud" target="_blank">pinata.cloud</a></li>
        <li>Click "Add Files" → "Folder" to upload your images folder</li>
        <li>Wait for upload to complete and copy the CID (e.g., QmXyz...)</li>
        <li>Repeat for your metadata folder</li>
        <li>Your image URLs will be: <code>ipfs://YOUR_CID/1.png</code></li>
      </ol>
    `
  },
  {
    id: 'folder-structure',
    title: 'Folder Structure',
    icon: FaFolder,
    content: `
      <h4>Recommended Folder Structure</h4>
      <p>Organize your collection files like this:</p>
      <pre class="code-block">
my-collection/
├── images/
│   ├── 1.png
│   ├── 2.png
│   ├── 3.png
│   └── ...
└── metadata/
    ├── 1.json
    ├── 2.json
    ├── 3.json
    └── ...</pre>

      <h4>File Naming</h4>
      <ul>
        <li>Use sequential numbers (1, 2, 3...) not zero-padded (001, 002)</li>
        <li>Keep filenames lowercase</li>
        <li>Match image and metadata numbers (1.png → 1.json)</li>
      </ul>

      <h4>Image Formats</h4>
      <ul>
        <li><strong>PNG:</strong> Best for pixel art and transparent backgrounds</li>
        <li><strong>JPG:</strong> Good for photos and detailed artwork</li>
        <li><strong>GIF:</strong> For animated NFTs (keep file size reasonable)</li>
        <li><strong>MP4:</strong> For video NFTs (max recommended: 50MB)</li>
      </ul>
    `
  },
  {
    id: 'metadata-format',
    title: 'Metadata Format',
    icon: FaCode,
    content: `
      <h4>Standard NFT Metadata</h4>
      <p>Each NFT needs a JSON metadata file. Here's the standard format:</p>
      <pre class="code-block">
{
  "name": "Cool Cat #1",
  "description": "A very cool cat from the Cool Cats collection",
  "image": "ipfs://QmYourImageCID/1.png",
  "attributes": [
    {
      "trait_type": "Background",
      "value": "Blue"
    },
    {
      "trait_type": "Fur",
      "value": "Orange"
    },
    {
      "trait_type": "Eyes",
      "value": "Happy"
    },
    {
      "trait_type": "Accessory",
      "value": "Crown"
    },
    {
      "trait_type": "Rarity Score",
      "value": 85,
      "display_type": "number"
    }
  ]
}</pre>

      <h4>Required Fields</h4>
      <ul>
        <li><strong>name:</strong> The name of this specific NFT</li>
        <li><strong>image:</strong> IPFS URL to the image file</li>
      </ul>

      <h4>Optional Fields</h4>
      <ul>
        <li><strong>description:</strong> A text description of the NFT</li>
        <li><strong>attributes:</strong> Array of traits/properties</li>
        <li><strong>animation_url:</strong> URL to video/audio file</li>
        <li><strong>external_url:</strong> Link to your website</li>
      </ul>
    `
  },
  {
    id: 'generative-art',
    title: 'Generative Art',
    icon: FaPalette,
    content: `
      <h4>Creating Generative Collections</h4>
      <p>Generative art collections combine different layers to create unique NFTs automatically.
      Popular collections like Bored Ape Yacht Club use this technique.</p>

      <h4>Popular Tools</h4>
      <div class="service-cards">
        <div class="service-card">
          <strong>HashLips Art Engine</strong>
          <p>Free, open-source, very popular</p>
          <a href="https://github.com/HashLips/hashlips_art_engine" target="_blank" rel="noopener noreferrer">GitHub <span class="external-icon">↗</span></a>
        </div>
        <div class="service-card">
          <strong>NFT Art Generator</strong>
          <p>No-code solution, beginner friendly</p>
          <a href="https://nft-generator.art" target="_blank" rel="noopener noreferrer">nft-generator.art <span class="external-icon">↗</span></a>
        </div>
        <div class="service-card">
          <strong>Bueno</strong>
          <p>Professional tool, easy to use</p>
          <a href="https://bueno.art" target="_blank" rel="noopener noreferrer">bueno.art <span class="external-icon">↗</span></a>
        </div>
      </div>

      <h4>Layer Structure</h4>
      <p>Organize your artwork into layers:</p>
      <pre class="code-block">
layers/
├── Background/
│   ├── Blue.png
│   ├── Red.png
│   └── Green.png
├── Body/
│   ├── Normal.png
│   ├── Zombie.png
│   └── Robot.png
├── Eyes/
│   ├── Happy.png
│   ├── Sad.png
│   └── Angry.png
└── Accessories/
    ├── Hat.png
    ├── Crown.png
    └── None.png</pre>

      <h4>Rarity Tips</h4>
      <ul>
        <li>Create more variations of common traits</li>
        <li>Rare traits should have fewer files (e.g., 1 golden vs 10 normal)</li>
        <li>Consider trait compatibility (some traits might not work together)</li>
      </ul>
    `
  },
  {
    id: 'ito-setup',
    title: 'Setting Up Your ITO',
    icon: FaCoins,
    content: `
      <h4>What is an ITO?</h4>
      <p>ITO (Initial Token Offering) is KleverChain's native minting mechanism.
      It allows users to mint NFTs directly from your collection for a set price.</p>

      <h4>Key Settings</h4>
      <ul>
        <li><strong>Receiver Address:</strong> Where sale funds will be sent</li>
        <li><strong>Payment Currency:</strong> Usually KLV, but can be other tokens</li>
        <li><strong>Max Amount:</strong> Total NFTs available for minting</li>
        <li><strong>Pack Pricing:</strong> Different quantities at different prices</li>
      </ul>

      <h4>Pack Pricing Strategy</h4>
      <p><strong>Important:</strong> On KleverChain, pack price is set <em>per NFT</em>, not as a total.
      The blockchain automatically calculates the total cost (NFTs × Price per NFT).</p>
      <p>Offer volume discounts to encourage larger purchases:</p>
      <table class="pricing-table">
        <thead>
          <tr>
            <th>Pack Size</th>
            <th>Price/NFT</th>
            <th>Total Cost</th>
            <th>Discount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1 NFT</td>
            <td>100 KLV</td>
            <td>100 KLV</td>
            <td>-</td>
          </tr>
          <tr>
            <td>5 NFTs</td>
            <td>90 KLV</td>
            <td>450 KLV</td>
            <td>10%</td>
          </tr>
          <tr>
            <td>10 NFTs</td>
            <td>80 KLV</td>
            <td>800 KLV</td>
            <td>20%</td>
          </tr>
        </tbody>
      </table>
      <p class="note">When configuring ITO, enter the <strong>price per NFT</strong> for each pack.
      The total is calculated automatically.</p>

      <h4>Best Practices</h4>
      <ul>
        <li>Start with a reasonable price - you can always lower it later</li>
        <li>Consider your target audience and their budget</li>
        <li>Promote your mint date on social media</li>
        <li>Have a whitelist or early access for community members</li>
      </ul>
    `
  },
  {
    id: 'best-practices',
    title: 'Best Practices',
    icon: FaStar,
    content: `
      <h4>Before Launch</h4>
      <ul>
        <li><strong>Test everything:</strong> Upload test metadata and verify it displays correctly</li>
        <li><strong>Build community:</strong> Create Discord/Twitter before launch</li>
        <li><strong>Verify URIs:</strong> Make sure all IPFS links work</li>
        <li><strong>Plan marketing:</strong> Announce mint date in advance</li>
      </ul>

      <h4>Collection Quality</h4>
      <ul>
        <li>High-resolution images (1000x1000 or higher recommended)</li>
        <li>Consistent style across all NFTs</li>
        <li>Clear and organized trait naming</li>
        <li>Unique and interesting combinations</li>
      </ul>

      <h4>Community Building</h4>
      <ul>
        <li>Engage with holders regularly</li>
        <li>Create utility for your NFTs (access, benefits)</li>
        <li>Be transparent about your roadmap</li>
        <li>Deliver on promises</li>
      </ul>

      <h4>Common Mistakes to Avoid</h4>
      <ul>
        <li>❌ Launching without testing</li>
        <li>❌ Over-pricing your collection</li>
        <li>❌ No social media presence</li>
        <li>❌ Copying other collections</li>
        <li>❌ Making promises you can't keep</li>
      </ul>

      <h4>Security Tips</h4>
      <ul>
        <li>Never share your wallet seed phrase</li>
        <li>Use a separate wallet for receiving funds</li>
        <li>Verify all contract interactions before signing</li>
        <li>Back up your original art files</li>
      </ul>
    `
  }
];

const CreatorGuide = () => {
  const [expandedSections, setExpandedSections] = useState(['getting-started']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const expandAll = () => {
    setExpandedSections(GUIDE_SECTIONS.map(s => s.id));
  };

  const collapseAll = () => {
    setExpandedSections([]);
  };

  return (
    <div className="creator-guide">
      <div className="guide-header">
        <div className="guide-title">
          <FaBookOpen className="guide-icon" />
          <div>
            <h2>Creator Guide</h2>
            <p>Everything you need to know to launch your NFT collection</p>
          </div>
        </div>
        <div className="guide-actions">
          <button className="expand-btn" onClick={expandAll}>
            Expand All
          </button>
          <button className="expand-btn" onClick={collapseAll}>
            Collapse All
          </button>
        </div>
      </div>

      <div className="guide-sections">
        {GUIDE_SECTIONS.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.id);

          return (
            <div
              key={section.id}
              className={`guide-section ${isExpanded ? 'expanded' : ''}`}
            >
              <button
                className="section-header"
                onClick={() => toggleSection(section.id)}
              >
                <div className="section-title">
                  <Icon className="section-icon" />
                  <span>{section.title}</span>
                </div>
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              {isExpanded && (
                <div
                  className="section-content"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="guide-footer">
        <div className="help-box">
          <FaLightbulb />
          <div>
            <strong>Need more help?</strong>
            <p>Join our Telegram community for support.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorGuide;
